import { attributionPayload } from '../../lib/attribution';
import { marketingPayload } from '../../lib/marketing';
/**
 * Where the buy-box leaves the count of upsell offers the server dropped, for the confirmation page to
 * pick up (feature 0042 lot D). A one-hop handoff across a full navigation — sessionStorage, so it dies
 * with the tab and can never resurface on a later order.
 */
export const DROPPED_UPSELLS_KEY = 'aqx.upsell.dropped.v1';
/**
 * Upper bound on the checkout POST. Order placement is normally sub-second; but if the backend ACCEPTS the
 * connection then hangs (saturated pool, slow re-price, advisory-lock contention) the fetch would otherwise
 * pend for ~300s (undici default), freezing the "Commander" button with no recovery but a page reload (which
 * discards the typed form). On timeout we surface a RETRYABLE error; the idempotency key makes the retry safe.
 */
const SUBMIT_TIMEOUT_MS = 20_000;
/**
 * Submit one COD order. `fetchImpl` is injectable for tests; `onPlaceOrder` is the clearly-marked
 * fallback used when the checkout proxy can't be reached (so the flow is end-to-end demonstrable before
 * the backend wiring lands).
 */
export async function placeCodOrder(args, opts = {}) {
    const doFetch = opts.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    // No fetch available at all (SSR / very old env) → placeholder so the UI still resolves.
    if (!doFetch) {
        await opts.onPlaceOrder?.(args);
        return { kind: 'placeholder' };
    }
    // Map the inline COD form → the checkout proxy body. The proxy + backend re-price; we send no money.
    // Name-split: a COD funnel legitimately collects a single name, but the backend requires a non-blank
    // lastName — so a single token doubles as the last name (never sent blank). `zip`/`country` keys are
    // OMITTED (not sent as ""): the server defaults zip and the store's country; sending "" would fail the
    // backend @Pattern("[A-Za-z]{2}") on country.
    const fullName = args.form.name.trim();
    const sp = fullName.indexOf(' ');
    const firstName = sp === -1 ? fullName : fullName.slice(0, sp);
    const lastName = sp === -1 ? fullName : fullName.slice(sp + 1).trim() || fullName;
    // Read once, before the request is composed: never throws, returns undefined when the tab has nothing.
    const attribution = attributionPayload();
    // Feature 0044 — the Meta-pixel context (fbp/fbc cookies, landing fbclid, page URL, own-traffic flag).
    // Attached on EVERY checkout, app installed or not: the row it becomes server-side is also the
    // "came from the storefront" marker the Conversions-API send gates on. Content, not identity — the
    // server drops anything that does not match the exact shapes Meta's own script produces.
    const marketing = marketingPayload();
    // The buyer's email, when the seller's form asked for one and the buyer typed something that is an
    // address (`buyerEmailOf` already applied the same bar the backend's `@Email` uses). Sent as the order's
    // OWN email — the field the order page shows and every journey reads to decide who to write to.
    const email = (args.form.email ?? '').trim() || null;
    const body = {
        body: {
            customer: { name: args.form.name, phone: args.form.phone || null, email },
            shippingAddress: {
                firstName,
                lastName,
                phone: args.form.phone || null,
                line1: args.form.address,
                city: args.form.city,
            },
            billingSameAsShipping: true,
            lines: args.lines && args.lines.length > 0
                ? args.lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity }))
                : [{ variantId: args.variantId, quantity: args.quantity }],
            note: args.form.note || null,
            // feature 0026 C2 S5: only stamp paymentMethod for a CARD order — an absent key = the backend's COD
            // default, so the COD request body stays byte-for-byte identical. The checkout proxy forwards the body
            // verbatim (runtime pass-through), so no shared type change is needed here.
            ...(args.paymentMethod === 'CARD' ? { paymentMethod: 'CARD' } : {}),
            // Feature 0035 slice A. The one-page COD funnel has no cart and no checkout page, so this is the ONLY
            // moment its provenance can be attached — and it is the dominant funnel for this merchant base.
            ...(attribution ? { attribution } : {}),
            ...(marketing ? { marketing } : {}),
            // Feature 0042 lot D — the accepted upsell offers, alongside `lines`, never inside it. Omitted entirely
            // when the buyer took none, so an ordinary order's body is unchanged.
            ...(args.upsell && args.upsell.accepted.length > 0 ? { upsell: args.upsell } : {}),
            // Feature 0014 — the applied promo code; omitted when none, same "unchanged ordinary body" rule.
            ...(args.discountCode ? { discountCode: args.discountCode } : {}),
        },
        idempotencyKey: args.idempotencyKey,
        // Dev store hint so the proxy can resolve the tenant on localhost (ignored in prod). See PlaceOrderArgs.store.
        store: args.store ?? null,
    };
    let res;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), SUBMIT_TIMEOUT_MS);
    try {
        res = await doFetch('/api/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: ctrl.signal,
        });
    }
    catch {
        // A TIMEOUT (we aborted) means the backend may be hung and the order might ALREADY exist — so surface a
        // RETRYABLE error, NOT the demo placeholder (which reads as success). The idempotency key makes the retry
        // safe. A genuine unreachable proxy (builder preview / no Astro server / offline) still falls back to the
        // placeholder so that flow completes.
        if (ctrl.signal.aborted)
            return { kind: 'error' };
        await opts.onPlaceOrder?.(args);
        return { kind: 'placeholder' };
    }
    finally {
        clearTimeout(timer);
    }
    if (res.ok) {
        try {
            const data = (await res.json());
            if (data.kind === 'ok') {
                return {
                    kind: 'ok',
                    orderId: data.data?.orderId,
                    orderNumber: data.data?.orderNumber,
                    total: data.data?.total,
                    paymentStatus: data.data?.paymentStatus,
                    // Threaded through as-is: ABSENT when the server said nothing, rather than an invented empty
                    // array — the outcome should not claim to know something it was not told.
                    droppedUpsellOffers: data.data?.droppedUpsellOffers,
                };
            }
            return { kind: 'error' };
        }
        catch {
            return { kind: 'error' };
        }
    }
    if (res.status === 422) {
        try {
            const err = (await res.json());
            if (err.kind === 'validation-error' && err.fieldErrors) {
                return { kind: 'validation-error', fieldErrors: err.fieldErrors };
            }
            // A phone-blocked order (feature 0047) comes back with the seller's own message — show it verbatim.
            const message = typeof err.message === 'string' ? err.message.trim() : '';
            if (message)
                return { kind: 'refused', message };
        }
        catch {
            /* fall through */
        }
        return { kind: 'error' };
    }
    return { kind: 'error' };
}
//# sourceMappingURL=submit.js.map